import { useState, useRef, useEffect } from 'react';
import { Trash, RotateCw } from 'lucide-react';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import { sortInstancesByLayerOrder } from '@utils/layerUtils';
import LayersPanel from './LayersPanel';
import './PrintAreaCanvas.css';

export default function PrintAreaCanvas() {
    const {
        activePrintAreaId,
        printAreas,
        logoInstances,
        logos,
        activeLogoInstanceId,
        updateLogoInstance,
        removeLogoInstance,
        textInstances,
        activeTextInstanceId,
        updateTextInstance,
        removeTextInstance,
        setIsInteracting,
        selectLayer,
        currentModel
    } = useConfiguratorStore();

    const canvasRef = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState(false);
    const [resizing, setResizing] = useState<string | null>(null);
    const [rotating, setRotating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null); // New state for text editing
    const dragStart = useRef({ x: 0, y: 0 }); // Changed to useRef for sync access
    const [initialScale, setInitialScale] = useState(1);
    const [initialRotation, setInitialRotation] = useState(0);

    // Auto-focus text element when editing starts
    useEffect(() => {
        if (editingId) {
            // Small timeout to ensure render cycle completes and element is editable
            setTimeout(() => {
                const el = document.getElementById(`text-inst-${editingId}`);
                if (el) {
                    el.focus();
                }
            }, 0);
        }
    }, [editingId]);

    const [startAngle, setStartAngle] = useState(0); // For radial rotation

    // Resize Observer State - Moved to top to avoid Hook Rule violation
    // Use reasonable default instead of 1x1 to prevent wrong handle sizes on first render
    const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current) return;
        
        // Initialize with actual size immediately
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            setContainerSize({ width: rect.width, height: rect.height });
        }
        
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (width > 0 && height > 0) {
                    setContainerSize({ width, height });
                }
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);


    const activePrintArea = printAreas.find(pa => pa.id === activePrintAreaId);
    const activeLogoInstances = logoInstances.filter(li => li.printAreaId === activePrintAreaId);
    const activeTextInstances = textInstances.filter(ti => ti.printAreaId === activePrintAreaId);

    if (!activePrintArea) {
        return (
            <div className="print-area-canvas empty">
                <div className="empty-state">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <rect x="8" y="8" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                    </svg>
                    <p>Select a print area to start</p>
                </div>
            </div>
        );
    }

    const handleLogoPointerDown = (e: React.PointerEvent, instanceId: string) => {
        e.preventDefault();
        e.stopPropagation();
        selectLayer('logo', instanceId);
        setDragging(true);
        setIsInteracting(true); // Prevent history push during drag
        dragStart.current = { x: e.clientX, y: e.clientY }; // Update ref
        (e.target as Element).setPointerCapture(e.pointerId);
    };

    // No group helper needed: updates target the single instance (including 'all-faces')

    const handleTextPointerDown = (e: React.PointerEvent, instanceId: string) => {
        e.preventDefault();
        e.stopPropagation();
        selectLayer('text', instanceId);
        setDragging(true);
        setIsInteracting(true); // Prevent history push during drag
        dragStart.current = { x: e.clientX, y: e.clientY }; // Update ref
        (e.target as Element).setPointerCapture(e.pointerId);
    };

    // Helper to calculate visual center based on rotation
    const calculateVisualCenter = (instance: { position: { x: number, y: number } }, rect: DOMRect) => {
        if (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') {
            // Rotated -90 deg mapping:
            // Visual X = Local Y
            // Visual Y = 1 - Local X
            return {
                centerX: rect.left + (instance.position.y * rect.width),
                centerY: rect.top + ((1 - instance.position.x) * rect.height)
            };
        }
        return {
            centerX: rect.left + (instance.position.x * rect.width),
            centerY: rect.top + (instance.position.y * rect.height)
        };
    };

    const handleResizeStart = (e: React.PointerEvent, corner: string) => {
        e.preventDefault();
        e.stopPropagation();
        setResizing(corner);
        setIsInteracting(true); // Prevent history push during resize
        dragStart.current = { x: e.clientX, y: e.clientY }; // Update ref
        (e.target as Element).setPointerCapture(e.pointerId);

        if (activeLogoInstanceId) {
            const instance = logoInstances.find(li => li.id === activeLogoInstanceId);
            if (instance) setInitialScale(instance.scale);
        } else if (activeTextInstanceId) {
            const instance = textInstances.find(ti => ti.id === activeTextInstanceId);
            if (instance) setInitialScale(instance.scale);
        }
    };

    const handleRotateStart = (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setRotating(true);
        setIsInteracting(true);
        (e.target as Element).setPointerCapture(e.pointerId);
        
        // Calculate center of the active element to determine start angle
        if (canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            let instance = null;

            if (activeLogoInstanceId) {
                instance = logoInstances.find(li => li.id === activeLogoInstanceId);
            } else if (activeTextInstanceId) {
                instance = textInstances.find(ti => ti.id === activeTextInstanceId);
            }

            if (instance) {
                setInitialRotation(instance.rotation);
                const { centerX, centerY } = calculateVisualCenter(instance, rect);
                
                // Calculate angle from center to mouse
                const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
                setStartAngle(angle);
            }
        }
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        // Shared Rotation Logic Helper
        const calculateNewRotation = (centerX: number, centerY: number) => {
            const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
            let deltaAngle = currentAngle - startAngle;

            // Normalize deltaAngle to be between -PI and PI to avoid wrapping jumps
            while (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
            while (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

            // Convert radians to degrees
             // Add to initial rotation
             let deg = initialRotation + (deltaAngle * (180 / Math.PI));
             // Normalize to 0-360 if needed, but Three.js handles overflow fine
             return deg;
        };

        // Handle Logo Interaction
        if (activeLogoInstanceId) {
            const instance = logoInstances.find(li => li.id === activeLogoInstanceId);
            if (!instance || !canvasRef.current) return;

            if (dragging) {
                const canvasRect = canvasRef.current.getBoundingClientRect();
                
                let deltaX = 0;
                let deltaY = 0;

                if (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') {
                    // Rotated -90 degrees:
                    // Screen Vertical Drag (Visual Y change) -> Logic X change.
                    // Screen Vertical range is rect.height.
                    deltaX = -(e.clientY - dragStart.current.y) / canvasRect.height; 

                    // Screen Horizontal Drag (Visual X change) -> Logic Y change.
                    // Screen Horizontal range is rect.width.
                    deltaY = (e.clientX - dragStart.current.x) / canvasRect.width; 
                } else {
                    deltaX = (e.clientX - dragStart.current.x) / canvasRect.width;
                    deltaY = (e.clientY - dragStart.current.y) / canvasRect.height;
                }
                
                updateLogoInstance(activeLogoInstanceId, {
                    position: {
                        x: instance.position.x + deltaX,
                        y: instance.position.y + deltaY
                    }
                });

                dragStart.current = { x: e.clientX, y: e.clientY };
            } else if (resizing) {
                // Smooth Distance-Based Scaling
                // Calculate center of object
                const rect = canvasRef.current.getBoundingClientRect();
                const { centerX, centerY } = calculateVisualCenter(instance, rect);

                // Calculate initial distance (from dragStart)
                const startDist = Math.hypot(dragStart.current.x - centerX, dragStart.current.y - centerY);
                // Calculate current distance
                const currentDist = Math.hypot(e.clientX - centerX, e.clientY - centerY);

                // Prevent division by zero
                if (startDist < 1) return;

                // Scale factor
                const scaleFactor = currentDist / startDist;
                
                // Allow higher max scale for All Faces print area
                const maxScale = activePrintAreaId === 'all-faces' ? 10 : 4;
                const newScale = Math.max(0.1, Math.min(maxScale, initialScale * scaleFactor));
                updateLogoInstance(activeLogoInstanceId, { scale: newScale });

            } else if (rotating) {
                 // Radial Rotation Logic
                 const rect = canvasRef.current.getBoundingClientRect();
                 const { centerX, centerY } = calculateVisualCenter(instance, rect);
                 
                 const newRotation = calculateNewRotation(centerX, centerY);
                 updateLogoInstance(activeLogoInstanceId, { rotation: newRotation });
            }
        }
        // Handle Text Interaction
        else if (activeTextInstanceId) {
            const instance = textInstances.find(ti => ti.id === activeTextInstanceId);
            if (!instance || !canvasRef.current) return;

            if (dragging) {
                const canvasRect = canvasRef.current.getBoundingClientRect();
                let deltaX = 0;
                let deltaY = 0;

                if (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') {
                    deltaX = -(e.clientY - dragStart.current.y) / canvasRect.height;
                    deltaY = (e.clientX - dragStart.current.x) / canvasRect.width;
                } else {
                    deltaX = (e.clientX - dragStart.current.x) / canvasRect.width;
                    deltaY = (e.clientY - dragStart.current.y) / canvasRect.height;
                }

                updateTextInstance(activeTextInstanceId, {
                    position: {
                        x: instance.position.x + deltaX,
                        y: instance.position.y + deltaY
                    }
                });

                dragStart.current = { x: e.clientX, y: e.clientY };
            } else if (resizing) {
                // Smooth Distance-Based Scaling for Text
                const rect = canvasRef.current.getBoundingClientRect();
                const { centerX, centerY } = calculateVisualCenter(instance, rect);

                const startDist = Math.hypot(dragStart.current.x - centerX, dragStart.current.y - centerY);
                const currentDist = Math.hypot(e.clientX - centerX, e.clientY - centerY);

                if (startDist < 1) return;

                const scaleFactor = currentDist / startDist;
                
                // Allow slightly larger max scale for text
                const newScale = Math.max(0.1, Math.min(8, initialScale * scaleFactor));
                updateTextInstance(activeTextInstanceId, { scale: newScale });

            } else if (rotating) {
                 // Radial Rotation Logic
                 const rect = canvasRef.current.getBoundingClientRect();
                 const { centerX, centerY } = calculateVisualCenter(instance, rect);
                 
                 const newRotation = calculateNewRotation(centerX, centerY);
                 updateTextInstance(activeTextInstanceId, { rotation: newRotation });
            }
        }
    };

    const handlePointerUp = () => {
        setDragging(false);
        setResizing(null);
        setRotating(false);
        setIsInteracting(false); // Re-enable history push
    };

    // Aspect ratio calculation removed (Fixed canvas size)
    // VIRTUAL CANVAS LOGIC
    // Calculate the visible bounds of the print area (UV space 0..1)
    const getVisibleBounds = () => {
        const { uvBounds } = activePrintArea;
        // Default to full 1x1 if no bounds
        if (!uvBounds) return { width: 1, height: 1, centerX: 0.5, centerY: 0.5 };

        const { minU, maxU, minV, maxV, needsFlipY } = uvBounds;
        
        // Helper for robust wrapping (matches render logic)
        const safeModulo = (val: number) => {
            const m = val % 1;
            if (m < 0 && m > -0.001) return 0;
            return (m + 1) % 1;
        };

        // Handle wrapping/overflow logic
        let width = maxU - minU;
        let height = maxV - minV;

        if (width > 0.99) width = 1;
        if (height > 0.99) height = 1;

        // Base UV Center
        // We normalize minU/minV to ensure we are calculating the center of the VISIBLE box 
        // within the 0..1 texture space.
        const normMinU = safeModulo(minU);
        const normMinV = safeModulo(minV);

        let uvCenterX = normMinU + width / 2;
        let uvCenterY = normMinV + height / 2;

        // Apply Inversion Logic to match Visual Render
        const useInvertX = activePrintArea.invertX ?? false;
        const useInvertY = activePrintArea.invertY ?? needsFlipY ?? false;

        // Calculate VISUAL Center (0..1 where 0,0 is Top-Left of Canvas)
        let visualCenterX = uvCenterX;
        let visualCenterY = uvCenterY;

        if (useInvertX) {
             // Invert X: 0 becomes 1, 1 becomes 0
             visualCenterX = 1 - uvCenterX;
        }

        if (!useInvertY) {
             // Standard (Three.js Bottom-Up): UV 0 is Bottom. Canvas 0 is Top.
             // So Visual Y = 1 - UV Y
             visualCenterY = 1 - uvCenterY;
        } 
        // Else useInvertY (Top-Down): UV 0 is Top. Matches Canvas. visualCenterY = uvCenterY.

                return { width, height, centerX: visualCenterX, centerY: visualCenterY };
    };

    const { width: boundsWidth, height: boundsHeight, centerX, centerY } = getVisibleBounds();

    // Calculate Zoom/Scale - RESPONSIVE
    const FILL_FACTOR = 0.85; 
    
    // ADJUST FOR ROTATION: Swap dimensions if rotated so we fit Visual Width to Container Width
    let effectiveWidth = boundsWidth;
    let effectiveHeight = boundsHeight;
    
    if (currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') {
        effectiveWidth = boundsHeight;
        effectiveHeight = boundsWidth;
    }
    
    // Scale Logic
    const scaleFitWidth = FILL_FACTOR / effectiveWidth;
    const containerAspect = containerSize.width / containerSize.height;
    const scaleFitHeight = (1 / containerAspect) * (FILL_FACTOR / effectiveHeight);

    const viewScale = Math.min(
        10,
        scaleFitWidth, 
        scaleFitHeight
    );

    // Calculate responsive handle sizes based on print area size
    // For small print areas (high zoom), scale down. For large areas (low zoom), also scale down.
    // Use print area bounds diagonal as the size metric
    const printAreaDiagonal = Math.sqrt(effectiveWidth * effectiveWidth + effectiveHeight * effectiveHeight);
    const handleScale = Math.min(1, printAreaDiagonal / 1.0); // Normalize: 1.0 diagonal = full size
    
    const handleSizeCorner = Math.max(6, Math.min(16, 12 * handleScale));
    const handleSizeSide = Math.max(5, Math.min(14, 10 * handleScale));
    const handleSizeCornerText = Math.max(5, Math.min(12, 9 * handleScale));
    const handleSizeSideText = Math.max(4, Math.min(10, 8 * handleScale));
    const borderWidth = Math.max(1, Math.min(3, 2.5 * handleScale));

    return (
        <div 
            ref={containerRef}
            className="print-area-canvas-container" 
            style={{ position: 'relative', overflow: 'hidden' }}
        >
            {/* 2D Canvas view - transformed to focus on print area */}
            <div
                ref={canvasRef}
                className="print-area-canvas"
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100%', 
                    height: '100%', // Fill container, no rigid aspect
                    
                    // Set transform origin to the Visual Center
                    transformOrigin: `${centerX * 100}% ${centerY * 100}%`,
                    
                    // Transform: Translate visualCenter to center, then scale, then rotate if Pen or Nylon Lanyard
                    transform: `translate(-${centerX * 100}%, -${centerY * 100}%) scale(${viewScale}) ${(currentModel === 'pen' || currentModel === 'square-pen' || currentModel === 'nylon-lanyard' || currentModel === 'dye-sublimated-lanyard-with-metal-crimp') ? 'rotate(-90deg)' : ''}` 
                }}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                {/* Visual Safe Zone Indicator - Improved for Wrapping */}
                {activePrintArea.uvBounds && (() => {
                    const { minU, maxU, minV, maxV, needsFlipY } = activePrintArea.uvBounds;

                    const useInvertX = activePrintArea.invertX ?? false;
                    const useInvertY = activePrintArea.invertY ?? needsFlipY ?? false;

                    const safeModulo = (val: number) => {
                        const m = val % 1;
                        if (m < 0 && m > -0.001) return 0;
                        return (m + 1) % 1;
                    };

                    const safeMinU = safeModulo(minU);
                    const safeMinV = safeModulo(minV);

                    let widthVal = (maxU - minU) * 100;
                    let heightVal = (maxV - minV) * 100;

                    let leftPct = safeMinU * 100;
                    let topPct = safeMinV * 100;

                    if (useInvertX) {
                         const normMax = ((maxU % 1) + 1) % 1;
                         leftPct = (1 - normMax) * 100;
                    }

                    if (!useInvertY) {
                         let normMax = ((maxV % 1) + 1) % 1;
                         // Fix: If maxV is exactly 1.0 (or integer), normMax becomes 0. 
                         // In Bottom-Up view (1-y), this makes topPct 100% (Bottom) instead of 0% (Top).
                         // We detect this edge case (effectively 0 but maxV is non-zero) and treat as 1.
                         if (normMax < 0.001 && maxV > 0.01) {
                             normMax = 1;
                         }
                         topPct = (1 - normMax) * 100;
                    }
                    if (widthVal > 95 && heightVal > 95) { // Force full coverage snap
                         if (widthVal > 95) { leftPct = 0; widthVal = 100; }
                         if (heightVal > 95) { topPct = 0; heightVal = 100; }
                    }

                    // Render Boxes (Handle Wrapping)
                    const boxes = [];
                    
                    // Box 1: Standard
                    boxes.push({ left: leftPct, top: topPct, width: widthVal, height: heightVal });
                    
                    // Check for Horizontal Wrap
                    if (leftPct + widthVal > 100.1) {
                         // Add Wrapped Segment (Start at 0)
                         const overflow = (leftPct + widthVal) - 100;
                         boxes.push({ left: 0, top: topPct, width: overflow, height: heightVal });
                         // Clamp Box 1 limit
                         boxes[0].width = 100 - leftPct;
                    }

                    return (
                        <>
                            {boxes.map((box, idx) => (
                                <div
                                    key={`safe-zone-${idx}`}
                                    style={{
                                        position: 'absolute',
                                        left: `${box.left}%`,
                                        top: `${box.top}%`,
                                        width: `${box.width}%`,
                                        height: `${box.height}%`,
                                        border: '2px dashed transparent', 
                                        backgroundColor: 'transparent',
                                        pointerEvents: 'none',
                                        zIndex: 10,
                                        boxSizing: 'border-box',
                                        // Hide left/right borders where split occurs?
                                        borderRightStyle: (idx === 0 && boxes.length > 1) ? 'none' : 'dashed',
                                        borderLeftStyle: (idx === 1) ? 'none' : 'dashed'
                                    }}
                                >
                                    {/* Label Removed as requested */}
                                </div>
                            ))}
                        </>
                    );
                })()}

                {/* Unified Sorted Rendering Loop */}
                {/* Capture active instance info for the shared handles overlay */}
                {(() => {
                    type OverlayInfo = {
                        type: 'logo' | 'text';
                        style: React.CSSProperties;
                        logoSrc?: string;
                        logoAlt?: string;
                        textContent?: string;
                        textFontStyles?: React.CSSProperties;
                        deleteId: string;
                        deleteFn: (id: string) => void;
                        handleSizeCorner: number;
                        handleSizeSide: number;
                    };
                    let activeOverlayInfo: OverlayInfo | null = null;

                    const items = sortInstancesByLayerOrder(activeLogoInstances, activeTextInstances, 'asc');

                    const renderedItems = items.map((item) => {
                    if (item.type === 'logo') {
                        // --- RENDER LOGO ---
                        const instance = item.data;
                        const logo = logos.find(l => l.id === instance.logoId);
                        if (!logo) return null;

                        const isActive = instance.id === activeLogoInstanceId;

                        const style: React.CSSProperties = {
                            position: 'absolute',
                            left: `${instance.position.x * 100}%`,
                            top: `${instance.position.y * 100}%`,
                            width: `${instance.scale * 30}%`, // Relative sizing
                            height: 'auto',
                            transform: `translate(-50%, -50%) rotate(${instance.rotation}deg)`,
                        };

                        // Capture active instance info for overlay (no z-index on instance)
                        if (isActive) {
                            activeOverlayInfo = {
                                type: 'logo',
                                style,
                                logoSrc: logo.processedImage,
                                logoAlt: logo.name,
                                deleteId: instance.id,
                                deleteFn: removeLogoInstance,
                                handleSizeCorner,
                                handleSizeSide,
                            };
                        }

                        return (
                            <div
                                key={instance.id}
                                className={`canvas-logo ${isActive ? 'active' : ''}`}
                                style={style}
                                onPointerDown={(e) => handleLogoPointerDown(e, instance.id)}
                            >
                                <img src={logo.processedImage} alt={logo.name} draggable={false} />
                            </div>
                        );
                    } else {
                        // --- RENDER TEXT ---
                        const textInst = item.data;
                        const isActive = textInst.id === activeTextInstanceId;
                        const isEditing = editingId === textInst.id;

                        // Extract position style to a variable (same pattern as logos)
                        const style: React.CSSProperties = {
                            position: 'absolute',
                            left: `${textInst.position.x * 100}%`,
                            top: `${textInst.position.y * 100}%`,
                            transform: `translate(-50%, -50%) rotate(${textInst.rotation}deg)`,
                        };

                        // Font styles needed for both the instance and the overlay sizing element
                        const textFontStyles: React.CSSProperties = {
                            fontFamily: textInst.fontFamily,
                            fontSize: `${textInst.fontSize * textInst.scale}px`,
                            fontWeight: textInst.fontStyle.includes('bold') ? 'bold' : 'normal',
                            fontStyle: textInst.fontStyle.includes('italic') ? 'italic' : 'normal',
                            textDecoration: textInst.fontStyle.includes('underline') ? 'underline' : 'none',
                            whiteSpace: 'pre',
                            padding: '4px',
                            minWidth: '1em',
                            textAlign: textInst.textAlign || 'center',
                        };

                        // Capture active instance info for overlay
                        if (isActive && !isEditing) {
                            activeOverlayInfo = {
                                type: 'text',
                                style,
                                textContent: textInst.text,
                                textFontStyles,
                                deleteId: textInst.id,
                                deleteFn: removeTextInstance,
                                handleSizeCorner: handleSizeCornerText,
                                handleSizeSide: handleSizeSideText,
                            };
                        }

                        return (
                            <div
                                key={textInst.id}
                                id={`text-inst-${textInst.id}`}
                                className={`text-instance ${isActive ? 'active' : ''} ${isEditing ? 'editing' : ''}`}
                                style={{
                                    ...style,
                                    ...textFontStyles,
                                    color: textInst.color,
                                    cursor: isEditing ? 'text' : (isActive ? 'move' : 'pointer'),
                                    userSelect: isEditing ? 'text' : 'none',
                                    outline: isEditing ? '1px dashed #3b82f6' : 'none',
                                }}
                                onPointerDown={(e) => {
                                    if (isEditing) {
                                        e.stopPropagation();
                                        return;
                                    }
                                    handleTextPointerDown(e, textInst.id);
                                }}
                                onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    setEditingId(textInst.id);
                                    selectLayer('text', textInst.id);
                                }}
                                contentEditable={isEditing}
                                suppressContentEditableWarning={true}
                                onBlur={(e) => {
                                    const newText = e.currentTarget.innerText;
                                    updateTextInstance(textInst.id, { text: newText });
                                    setEditingId(null);
                                }}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                }}
                            >
                                {textInst.text}
                            </div>
                        );
                    }
                    });

                    // Use container object to bypass TS closure-mutation narrowing
                    // (TS can't track that activeOverlayInfo was mutated inside .map())
                    const overlayRef = { current: activeOverlayInfo as OverlayInfo | null };
                    const overlay = overlayRef.current;

                    return (
                        <>
                            {renderedItems}

                            {/* Shared Active Instance Handles Overlay */}
                            {overlay != null && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 200,
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {/* Handle wrapper — matches active instance position/size */}
                                    <div
                                        style={{
                                            ...overlay.style,
                                            border: `${borderWidth}px solid #3b82f6`,
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        {/* Hidden sizing element — establishes correct bounding box */}
                                        {overlay.type === 'logo' && overlay.logoSrc && (
                                            <img
                                                src={overlay.logoSrc}
                                                alt=""
                                                aria-hidden="true"
                                                tabIndex={-1}
                                                draggable={false}
                                                style={{ width: '100%', visibility: 'hidden', display: 'block', pointerEvents: 'none' }}
                                            />
                                        )}
                                        {overlay.type === 'text' && (
                                            <span
                                                aria-hidden="true"
                                                tabIndex={-1}
                                                style={{ ...overlay.textFontStyles, visibility: 'hidden', pointerEvents: 'none' }}
                                            >
                                                {overlay.textContent}
                                            </span>
                                        )}

                                        {/* Resize Handles - Corners */}
                                        {['nw', 'ne', 'sw', 'se'].map(corner => (
                                            <div
                                                key={corner}
                                                className={`resize-handle ${corner}`}
                                                onPointerDown={(e) => handleResizeStart(e, 'se')}
                                                style={{
                                                    position: 'absolute',
                                                    width: `${overlay.handleSizeCorner}px`,
                                                    height: `${overlay.handleSizeCorner}px`,
                                                    background: 'white',
                                                    border: '2px solid #3b82f6',
                                                    borderRadius: '50%',
                                                    pointerEvents: 'auto',
                                                    ...(corner.includes('n') ? { top: `-${overlay.handleSizeCorner/2}px` } : { bottom: `-${overlay.handleSizeCorner/2}px` }),
                                                    ...(corner.includes('w') ? { left: `-${overlay.handleSizeCorner/2}px` } : { right: `-${overlay.handleSizeCorner/2}px` }),
                                                    cursor: `${corner}-resize`
                                                }}
                                            />
                                        ))}

                                        {/* Resize Handles - Sides (Middle) */}
                                        {['n', 's', 'e', 'w'].map(side => (
                                            <div
                                                key={side}
                                                className={`resize-handle ${side}`}
                                                onPointerDown={(e) => handleResizeStart(e, 'se')}
                                                style={{
                                                    position: 'absolute',
                                                    width: `${overlay.handleSizeSide}px`,
                                                    height: `${overlay.handleSizeSide}px`,
                                                    background: 'white',
                                                    border: '2px solid #3b82f6',
                                                    borderRadius: '50%',
                                                    pointerEvents: 'auto',
                                                    ...(side === 'n' ? { top: `-${overlay.handleSizeSide/2}px`, left: '50%', transform: 'translateX(-50%)' } : {}),
                                                    ...(side === 's' ? { bottom: `-${overlay.handleSizeSide/2}px`, left: '50%', transform: 'translateX(-50%)' } : {}),
                                                    ...(side === 'e' ? { right: `-${overlay.handleSizeSide/2}px`, top: '50%', transform: 'translateY(-50%)' } : {}),
                                                    ...(side === 'w' ? { left: `-${overlay.handleSizeSide/2}px`, top: '50%', transform: 'translateY(-50%)' } : {}),
                                                    cursor: `${side}-resize`
                                                }}
                                            />
                                        ))}

                                        {/* Rotate Handle */}
                                        <div
                                            onPointerDown={handleRotateStart}
                                            style={{
                                                position: 'absolute',
                                                top: overlay.type === 'logo' ? '-60px' : '-50px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                cursor: 'grab',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            <div style={{
                                                width: overlay.type === 'logo' ? '32px' : '24px',
                                                height: overlay.type === 'logo' ? '32px' : '24px',
                                                background: 'white',
                                                border: '1px solid #3b82f6',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}>
                                                <RotateCw size={overlay.type === 'logo' ? 18 : 14} color="#3b82f6" />
                                            </div>
                                            <div style={{ width: '1px', height: overlay.type === 'logo' ? '28px' : '25px', background: '#3b82f6' }}></div>
                                        </div>

                                        {/* Delete Button */}
                                        <button
                                            className="delete-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                overlay.deleteFn(overlay.deleteId);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: overlay.type === 'logo' ? '-32px' : '-25px',
                                                right: overlay.type === 'logo' ? '-32px' : '-25px',
                                                width: overlay.type === 'logo' ? '32px' : '24px',
                                                height: overlay.type === 'logo' ? '32px' : '24px',
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#ff4444',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            <Trash size={overlay.type === 'logo' ? 24 : 18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}


            </div>  {/* Close print-area-canvas */}

            {activePrintAreaId && <LayersPanel printAreaId={activePrintAreaId} />}
        </div>
    );
}
